import { lookupOverlays, lookupAddressRow, catalogBlockToResolvedOverlays, dedupeRefs, GRAPHQL_BATCH_REF_LIMIT, graphqlResolveAddressesBatch, isGraphQLRequestError, fetchAddressOverlaysBatch } from '../chunk-CYWVKDZM.js';
export { EMPTY_BATCH_OVERLAYS, boundOverlayRefs, catalogBlockToResolvedOverlays, dedupeRefs, fetchAddressOverlaysBatch, indexBatchEntries, indexPreparedEntries, isEmptyOverlays, isOverlayAnswerConfirmed, isSavedAddressRow, lookupAddressRow, lookupOverlays, mapWireHit, overlayRefKey, prepareBatchEntry, refsCacheKey, resolveAddressAcrossChains, toResolvedOverlays } from '../chunk-CYWVKDZM.js';
import { tierWeightToScope } from '../chunk-AUXCU4RA.js';
import { isDefaultedField } from '../chunk-LFINYFZE.js';
import '../chunk-4TM54R6Y.js';
import '../chunk-6V6OWGS3.js';
import '../chunk-MY2WTNZQ.js';
import '../chunk-WFOQQZAG.js';
import '../chunk-3KBLIIIM.js';
import '../chunk-XR6TNFQL.js';
import '../chunk-EE3BFQYH.js';
import { unwrapData } from '../chunk-HIWBYL4O.js';
import { brandEntityType } from '../chunk-IHYVRZAP.js';
import '../chunk-X34AGPO3.js';
import '../chunk-QZC2D6M4.js';
import '../chunk-7B3IWQPL.js';
import '../chunk-5WIUKZF6.js';
import '../chunk-HCZGW64N.js';
import '../chunk-54H267DL.js';
import '../chunk-2FJIJRTO.js';
import '../chunk-BFUJOKOR.js';
import '../chunk-DQNP4KXT.js';
import '../chunk-WVLHT3DL.js';
import '../chunk-BDQTZJ2T.js';
import '../chunk-ABMIRNDT.js';
import '../chunk-BJ3WKLB6.js';
import '../chunk-INX63VAT.js';
import '../chunk-N5XRRHZ7.js';
import { LEVEL_BY_SEVERITY, SCORE_BY_SEVERITY, SEVERITY_BY_LEVEL, RISK_LEVEL_RANK, SCORE_BY_LEVEL, worstBy, claimedRiskLevel, worstRisk, Severity } from '../chunk-UDOQQOJI.js';
import '../chunk-3QN2VZ56.js';
import '../chunk-C72Y6YZL.js';
import '../chunk-HVUWQL4C.js';
import '../chunk-O2TUCS6S.js';
import { normalizeAddress } from '../chunk-UW5NWLXH.js';
import '../chunk-5KYEBYP7.js';
import '../chunk-AHISJLEK.js';
import '../chunk-QXYP3KHC.js';
import '../chunk-DI5BDT3Z.js';
import { toCanonicalChainId } from '../chunk-E2IBHV7W.js';
import { createLogger } from '../chunk-GPJ572YS.js';
import '../chunk-N7SZ66QO.js';
import '../chunk-RAO7SHWG.js';
import '../chunk-SAW4RQG7.js';
import '../chunk-PZ5AY32C.js';

// src/overlays/risk.ts
function wireKey(value) {
  return typeof value === "string" ? value.toLowerCase().trim() : "";
}
function riskFromSeverity(severity) {
  return LEVEL_BY_SEVERITY[wireKey(severity)];
}
function scoreFromSeverity(severity) {
  return SCORE_BY_SEVERITY[wireKey(severity)];
}
function declaredRisk(value) {
  const key = wireKey(value);
  return SEVERITY_BY_LEVEL[key] ? key : void 0;
}
function riskForHit(severity, riskLevel) {
  const stated = riskFromSeverity(severity);
  const declared = declaredRisk(riskLevel);
  const level = stated && declared ? (RISK_LEVEL_RANK[declared] ?? 0) > (RISK_LEVEL_RANK[stated] ?? 0) ? declared : stated : stated ?? declared;
  if (!level) return void 0;
  return { level, score: SCORE_BY_LEVEL[level] ?? 0 };
}
function hitRisk(hit) {
  return riskForHit(hit.severity, hit.riskLevel);
}
function overlayRisk(hits) {
  return worstBy(hits, hitRisk, (risk) => risk.level);
}
function catalogRisk(overlays) {
  if (!overlays) return void 0;
  return overlayRisk([...overlays.tags, ...overlays.labels, ...overlays.groups ?? []]);
}
function addressRisk(rows, catalog) {
  const candidates = [];
  for (const row of rows) {
    if (isDefaultedField(row, "risk_level")) {
      continue;
    }
    const level = declaredRisk(row?.risk_level);
    if (level) {
      candidates.push({ level, score: SCORE_BY_LEVEL[level] ?? 0 });
    }
  }
  if (catalog) candidates.push(catalog);
  return worstBy(
    candidates,
    (r) => r,
    (r) => r.level
  );
}

// src/overlays/addressName.ts
var ABBREVIATED = /([A-Za-z0-9]{3,})\s?(?:\.{2,}|…)\s?([A-Za-z0-9]{2,})/g;
var LONG_TOKEN = /[A-Za-z0-9]{20,}/g;
var BARE_HEX = /^(?:0x)?[0-9a-f]{40,64}$/i;
var EDGE_PUNCTUATION = /^[\s:;,|/·\-–—]+|[\s:;,|/·\-–—]+$/g;
function stripEcho(text, address) {
  const own = address.trim().toLowerCase();
  if (!own) return text;
  const isOwn = (head, tail) => {
    const h = head.toLowerCase();
    const t = tail.toLowerCase();
    return h.replace(/^0x/, "").length >= 2 && own.startsWith(h) && own.endsWith(t);
  };
  const stripped = text.replace(ABBREVIATED, (match, head, tail) => isOwn(head, tail) ? "" : match).replace(LONG_TOKEN, (match) => match.toLowerCase() === own ? "" : match);
  if (stripped === text) return text;
  return stripped.replace(/\(\s*\)|\[\s*\]/g, "").replace(/\s{2,}/g, " ").replace(EDGE_PUNCTUATION, "");
}
function collapseRepeatedTail(text) {
  const words = text.split(" ");
  for (let size = Math.floor((words.length - 1) / 2); size >= 1; size -= 1) {
    const end = words.length;
    const tail = words.slice(end - size).join(" ").toLowerCase();
    const before = words.slice(end - 2 * size, end - size).join(" ").toLowerCase();
    if (tail === before) return collapseRepeatedTail(words.slice(0, end - size).join(" "));
  }
  return text;
}
function addressName(name, address) {
  let text = (name ?? "").replace(/\s+/g, " ").trim();
  if (!text) return null;
  if (BARE_HEX.test(text)) return null;
  if (address) text = stripEcho(text, address);
  text = collapseRepeatedTail(text.trim());
  return text ? text : null;
}

// src/overlays/toAddressOverlay.ts
function leading(hits, declared) {
  return hits.find((hit) => hit.catalogId === declared) ?? hits.find((hit) => hit.isPrimary) ?? hits[0];
}
function primaryEntityId(bundle) {
  const declared = bundle.primary?.find((p) => p.kind === "entity")?.catalogId;
  if (declared && bundle.entities.some((e) => e.id === declared)) return declared;
  return bundle.entities[0]?.id;
}
function toAddressOverlay(bundle, options = {}) {
  if (!bundle) return {};
  const primary = (kind) => bundle.primary?.find((p) => p.kind === kind)?.catalogId;
  const label = leading(bundle.labels, primary("label"));
  const tag = leading(bundle.tags, primary("tag"));
  const entity = bundle.entities.find((e) => e.id === primaryEntityId(bundle));
  const score = typeof options.riskScore === "number" && Number.isFinite(options.riskScore) ? options.riskScore : void 0;
  const rolledUp = overlayRisk([...bundle.tags, ...bundle.labels, ...bundle.groups]);
  const measured = claimedRiskLevel(void 0, score);
  const riskLevel = options.riskLevel ?? worstRisk([measured, rolledUp?.level]);
  const scoreLeads = score !== void 0 && (!rolledUp || measured !== void 0 && RISK_LEVEL_RANK[measured] >= RISK_LEVEL_RANK[rolledUp.level]);
  const entityName = addressName(entity?.name, bundle.address) ?? void 0;
  return {
    label: addressName(label?.name, bundle.address) ?? entityName,
    primaryEntityId: entity?.id,
    primaryEntityName: entityName,
    tags: [...new Set(bundle.tags.map((hit) => hit.name).filter((n) => Boolean(n)))],
    catalogGroups: [...bundle.groups].filter((hit) => hit.catalogId.trim()).sort((a, b) => Number(b.catalogId === primary("group")) - Number(a.catalogId === primary("group")) || Number(b.isPrimary) - Number(a.isPrimary)).map((hit) => ({ id: hit.catalogId, label: hit.name || hit.catalogId, ...hit.color ? { color: hit.color } : {} })),
    color: label?.color || tag?.color || entity?.color,
    logo: label?.logo || tag?.logo || entity?.logo,
    riskLevel,
    riskScore: scoreLeads ? score : rolledUp?.score ?? score,
    riskScoreBasis: scoreLeads ? "measured" : "category"
  };
}

// src/overlays/proxy.ts
function proxyContextKey(context) {
  return context ? JSON.stringify([toCanonicalChainId(context.chain_id), context.block_number, context.block_hash ?? null, context.tx_hash ?? null]) : "";
}
function addressProxySummary(value) {
  if (!value || typeof value !== "object") return void 0;
  const v = value;
  if (!["resolved", "incomplete", "unknown"].includes(v.status) || !["block_state", "latest_record"].includes(v.basis)) return void 0;
  return { ...v, implementations: Array.isArray(v.implementations) ? [...new Set(v.implementations.filter((a) => typeof a === "string" && a.length > 0))] : [] };
}
function proxySummaryForContext(summary, context) {
  if (!context) return summary;
  return summary?.basis === "block_state" && proxyContextKey(summary.context ?? void 0) === proxyContextKey(context) ? summary : void 0;
}

// src/overlays/abi.ts
function addressAbiSummary(value) {
  if (!value || typeof value !== "object") return void 0;
  const v = value;
  if (!["block_state", "latest_record"].includes(v.basis) || !Array.isArray(v.records)) return void 0;
  const records = v.records.filter((r) => r && typeof r.address === "string" && r.address.length > 0 && ["contract", "implementation"].includes(r.via) && ["recorded", "forensic"].includes(r.kind) && ["system", "customer", "org", "tenant", "user"].includes(r.scope));
  return records.length ? { ...v, records } : void 0;
}
function abiSummaryForContext(summary, context) {
  if (!context) return summary;
  return summary?.basis === "block_state" && proxyContextKey(summary.context ?? void 0) === proxyContextKey(context) ? summary : void 0;
}
function addressAbiLabel(summary) {
  if (summary.records.some((r) => r.scope !== "system")) return "Custom ABI";
  return summary.records.every((r) => r.kind === "forensic") ? "Forensic ABI" : "ABI";
}

// src/overlays/addressFacts.ts
function addressFactsOverlay(row) {
  if (!row) return {};
  const out = {};
  if (typeof row.chain === "string" && row.chain && typeof row.address === "string" && row.address) {
    out.addressFacts = {
      chainId: toCanonicalChainId(row.chain) ?? row.chain,
      address: normalizeAddress(row.address, row.chain),
      basis: "latest_record",
      ...typeof row.is_contract === "boolean" ? { isContract: row.is_contract } : {},
      ...typeof row.is_contract_verified === "boolean" ? { contractVerified: row.is_contract_verified } : {}
    };
  }
  if (row.is_contract_verified === true) out.contractVerified = true;
  const abi = addressAbiSummary(row.metadata?.abi);
  if (abi) out.abi = abi;
  const proxy = addressProxySummary(row.metadata?.proxy);
  if (proxy) out.proxy = proxy;
  return out;
}
function addressOverlayForContext(overlay, address, chain) {
  const facts = overlay?.addressFacts;
  if (!facts) return overlay;
  if (!chain || (toCanonicalChainId(chain) ?? chain) !== facts.chainId || normalizeAddress(address, chain) !== facts.address) return void 0;
  return overlay;
}

// src/overlays/resolver.ts
function overlayResolver(batch) {
  return (address, chainId) => {
    const bundle = lookupOverlays(batch, address, chainId);
    const row = lookupAddressRow(batch, address, chainId);
    if (!bundle && !row) return void 0;
    const risk = addressRisk([row], catalogRisk(bundle));
    return {
      ...toAddressOverlay(bundle),
      ...addressFactsOverlay(row),
      ...risk ? { riskLevel: risk.level, riskScore: risk.score, riskScoreBasis: "category" } : {}
    };
  };
}

// src/overlays/rest.ts
function ref(chain, address) {
  return `/addresses/${encodeURIComponent(chain)}/${encodeURIComponent(address)}`;
}
function restQuery(args) {
  const query = {};
  if (args.withScopes) query.scopes = "true";
  if (args.scope && args.scope !== "USER") query.scope = args.scope;
  return Object.keys(query).length > 0 ? query : void 0;
}
async function fetchAddressOverlaysRest(client, args, opts) {
  const chain = args.chain ?? void 0;
  if (!chain) return null;
  const res = unwrapData(
    await client.get(ref(chain, args.address), {
      query: restQuery(args),
      signal: opts?.signal,
      cacheTtlMs: 0
    })
  );
  const block = res?.catalog ?? void 0;
  return catalogBlockToResolvedOverlays(block, { address: args.address, chainId: chain });
}
async function fetchAddressWithOverlays(client, args, opts) {
  const chain = args.chain ?? void 0;
  if (!chain) return null;
  const res = unwrapData(
    await client.get(ref(chain, args.address), {
      query: restQuery(args),
      signal: opts?.signal,
      cacheTtlMs: 0
    })
  );
  return {
    address: res?.address ?? null,
    saved: Boolean(res?.saved),
    overlays: catalogBlockToResolvedOverlays(res?.catalog ?? void 0, {
      address: args.address,
      chainId: chain
    })
  };
}

// src/overlays/batchRouted.ts
var log = createLogger("fe-commons:overlays");
async function fetchAddressOverlaysBatchRouted(client, refs, route, opts = {}) {
  const { graphqlClient } = route;
  if (!opts.proxyContext && !refs.some((ref2) => ref2.asOf !== void 0 || ref2.blockNumber !== void 0) && route.transport === "graphql" && graphqlClient && dedupeRefs(refs).length <= GRAPHQL_BATCH_REF_LIMIT) {
    try {
      return await graphqlResolveAddressesBatch(graphqlClient, refs, opts, { signal: opts.signal });
    } catch (err) {
      if (!isGraphQLRequestError(err)) throw err;
      log.warn("address.overlays batch GraphQL failed, falling back to REST", {
        status: err.status,
        message: err.message
      });
    }
  }
  return fetchAddressOverlaysBatch(client, refs, opts);
}

// src/overlays/txCatalog.ts
var RESOLVE_PATH = "/transactions/catalog/resolve";
var RESOLVE_REF_LIMIT = 500;
var RESOLVE_CONCURRENCY = 3;
var EMPTY_TX_CATALOG_BATCH = { byTxId: /* @__PURE__ */ new Map() };
function lookupTxCatalog(batch, txId) {
  return txId ? batch.byTxId.get(txId) : void 0;
}
function dedupeTxRefs(refs) {
  const seen = /* @__PURE__ */ new Set();
  const out = [];
  for (const ref2 of refs) {
    const txId = (ref2.txId ?? "").trim();
    if (!txId || seen.has(txId)) continue;
    seen.add(txId);
    out.push({ txId, chainId: ref2.chainId ?? null, ...ref2.asOf !== void 0 ? { asOf: ref2.asOf } : {}, ...ref2.blockNumber !== void 0 ? { blockNumber: ref2.blockNumber } : {} });
  }
  return out;
}
async function resolveChunk(client, refs, opts) {
  const body = {
    refs: refs.map((r) => ({ tx_id: r.txId, ...r.chainId ? { chain_id: r.chainId } : {}, ...r.asOf !== void 0 ? { as_of: r.asOf } : {}, ...r.blockNumber !== void 0 ? { block_number: r.blockNumber } : {} })),
    ...opts?.withScopes ? { scopes: true } : {},
    // `USER` is the caller's own view, which is what omitting it already means.
    // Sending it anyway would be a distinct request body for an identical read.
    ...opts?.scope && opts.scope !== "USER" ? { scope: opts.scope } : {}
  };
  const res = unwrapData(
    await client.post(RESOLVE_PATH, body, { signal: opts?.signal })
  );
  return res?.resolved ?? [];
}
async function fetchTxCatalogBatch(client, refs, opts) {
  const cleaned = dedupeTxRefs(refs);
  if (cleaned.length === 0) return EMPTY_TX_CATALOG_BATCH;
  const chunks = [];
  for (let i = 0; i < cleaned.length; i += RESOLVE_REF_LIMIT) {
    chunks.push(cleaned.slice(i, i + RESOLVE_REF_LIMIT));
  }
  const byTxId = /* @__PURE__ */ new Map();
  for (let i = 0; i < chunks.length; i += RESOLVE_CONCURRENCY) {
    const wave = await Promise.all(
      chunks.slice(i, i + RESOLVE_CONCURRENCY).map((chunk) => resolveChunk(client, chunk, opts))
    );
    for (const entries of wave) {
      for (const entry of entries) {
        if (!entry?.tx_id) continue;
        byTxId.set(
          entry.tx_id,
          catalogBlockToResolvedOverlays(entry.catalog ?? void 0, { address: entry.tx_id })
        );
      }
    }
  }
  return { byTxId };
}

// src/overlays/bundle.ts
function hitLabel(hit) {
  return hit.name ?? hit.catalogId;
}
function primaryFirst(hits) {
  return [...hits].sort((a, b) => Number(b.isPrimary) - Number(a.isPrimary));
}
function glyph(hit) {
  return { logo: hit.logo, color: hit.color };
}
function why(hit) {
  return {
    confidence: hit.confidence,
    explanation: hit.explanation.length > 0 ? hit.explanation : void 0,
    lifecycle: hit.lifecycle
  };
}
function overlayLabelRefs(hits) {
  return primaryFirst(hits).map((hit) => ({
    label: hitLabel(hit),
    // The provenance dot: which tier won this overlay (yours / your tenant's / a
    // provider's). Same derivation as the chips, so a row agrees with the panel.
    scope: tierWeightToScope(hit.tierWeight),
    // Two different questions about the same chip: `scope` is who says so,
    // `severity` is how bad it is. A label answers both.
    ...severityRef(hit),
    ...glyph(hit),
    ...why(hit)
  }));
}
function overlayTagRefs(hits) {
  return primaryFirst(hits).map((hit) => ({
    label: hitLabel(hit),
    ...severityRef(hit),
    ...glyph(hit),
    ...why(hit)
  }));
}
function severityRef(hit) {
  const key = typeof hit.severity === "string" ? hit.severity.toLowerCase() : "";
  return Object.values(Severity).includes(key) ? { severity: key } : {};
}
function overlayGroupRefs(hits) {
  return primaryFirst(hits).map((hit) => ({
    id: hit.catalogId,
    name: hit.name,
    ...glyph(hit),
    ...why(hit)
  }));
}
function overlayEntityRef(overlays) {
  const [first] = overlays.entities;
  if (!first) return null;
  return {
    id: first.id,
    name: first.name ?? first.id,
    entityType: first.type,
    logo: first.logo,
    color: first.color,
    // An address→entity link is scored like any other assignment; the bundle
    // carries no bullets for it, so the hover states the score alone.
    confidence: first.confidence
  };
}
function overlayCounterpartyCategory(overlays) {
  const headline = overlayEntityRef(overlays)?.entityType ?? null;
  if (headline && headline !== "other") return headline;
  const specific = new Set(
    overlays.entities.map((entity) => entity.type).filter((type) => !!type && type !== "other")
  );
  if (specific.size > 0) return specific.size === 1 ? [...specific][0] : headline;
  const branded = new Set(
    overlays.entities.map((entity) => brandEntityType(entity.name)).filter((type) => !!type && type !== "other")
  );
  return branded.size === 1 ? [...branded][0] : headline;
}
function overlayCatalogBundle(overlays) {
  return {
    labels: overlayLabelRefs(overlays.labels),
    tags: overlayTagRefs(overlays.tags),
    groups: overlayGroupRefs(overlays.groups),
    entity: overlayEntityRef(overlays)
  };
}
function catalogBundleChips(bundle) {
  return {
    entities: bundle.entity ? [entityChip(bundle.entity)] : [],
    labels: (bundle.labels ?? []).map(labelChip),
    tags: (bundle.tags ?? []).map(tagChip),
    groups: (bundle.groups ?? []).map(groupChip)
  };
}
function textChipId(label, index) {
  return `${label}-${index}`;
}
function labelChip(ref2, index) {
  return {
    id: textChipId(ref2.label, index),
    name: ref2.label,
    value: ref2.value,
    // Two different questions about one chip: `scope` is who says so, `severity`
    // is how bad it is. A label answers both — an explorer's phishing or
    // sanctions claim arrives as a label as often as a tag, and a row that drops
    // it draws `Sanctioned: OFAC` exactly like `Uniswap: Router`.
    scope: ref2.scope,
    severity: ref2.severity,
    ...glyphOf(ref2),
    ...whyOf(ref2)
  };
}
function tagChip(ref2, index) {
  return {
    id: textChipId(ref2.label, index),
    name: ref2.label,
    severity: ref2.severity,
    ...glyphOf(ref2),
    ...whyOf(ref2)
  };
}
function groupChip(ref2) {
  return {
    id: ref2.id,
    // A group IS identified by id, so an unnamed one shows the id rather than
    // an empty chip.
    name: ref2.name ?? ref2.id,
    count: ref2.count,
    ...glyphOf(ref2),
    ...whyOf(ref2)
  };
}
function entityChip(ref2) {
  return {
    id: ref2.id,
    name: ref2.name || ref2.id,
    entityType: ref2.entityType,
    isPrimary: ref2.isPrimary,
    ...glyphOf(ref2),
    ...whyOf(ref2)
  };
}
function glyphOf(ref2) {
  return { logo: ref2.logo, color: ref2.color };
}
function whyOf(ref2) {
  return { confidence: ref2.confidence, explanation: ref2.explanation, lifecycle: ref2.lifecycle };
}

export { EMPTY_TX_CATALOG_BATCH, abiSummaryForContext, addressAbiLabel, addressAbiSummary, addressFactsOverlay, addressName, addressOverlayForContext, addressProxySummary, addressRisk, catalogBundleChips, catalogRisk, declaredRisk, dedupeTxRefs, fetchAddressOverlaysBatchRouted, fetchAddressOverlaysRest, fetchAddressWithOverlays, fetchTxCatalogBatch, lookupTxCatalog, overlayCatalogBundle, overlayCounterpartyCategory, overlayEntityRef, overlayGroupRefs, overlayLabelRefs, overlayResolver, overlayRisk, overlayTagRefs, proxyContextKey, proxySummaryForContext, riskForHit, riskFromSeverity, scoreFromSeverity, toAddressOverlay };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map